import { Typography } from '../Typography';
import ReactDom from 'react-dom';
import { syncManager } from '../../utils/syncManager';
import { MAINTENANCE_COOKIES_CHANGED, MAINTENANCE_MODAL_WARNING_KEY } from './const';
import { MaintenanceProps } from './types';
import { FC, useEffect, useState } from 'react';
import { useStoragePerUserData, useUserPageContextState } from '../../modules/user';
import { ModalBox } from 'components/ModalBox';
import { MaintenanceWarningModal } from './MaintenanceWarningModal';
import { yellow } from '../Color';
import { useTranslationAPI } from '../../modules/translations';
import { connectModuleCodes } from '../../data/translations/connectModuleCodes';
import { ExclamationCircle } from '../Icons/ExclamationCircle';
import { FlexGrid } from '../FlexGrid';
import { formatMaintenanceDateTime, normalizeBoolean, parseMaintenanceDateTime } from './utils';
import Countdown from 'react-countdown';
import { codes } from '../../data/translations';
import { PaddingBox } from '../PaddingBox';

const MaintenanceCountdown: FC<Pick<MaintenanceProps, 'maintenanceDateTimeUtc'>> = (
	{
		maintenanceDateTimeUtc,
	},
) => {
	const { translate } = useTranslationAPI();

	return (
		<Countdown
			date={parseMaintenanceDateTime(maintenanceDateTimeUtc)}
			intervalDelay={0}
			precision={2}
			renderer={({ hours, minutes, seconds, completed }) => {
				if (completed) {
					return (
						<>
							{translate(connectModuleCodes.platformHasBeenDisabledSince)}{' '}{formatMaintenanceDateTime(maintenanceDateTimeUtc)}
						</>
					);
				} else {
					return (
						<span>
							{translate(connectModuleCodes.platformWillBeDisabledIn)}{' '}
							{hours}{translate(codes.dateTime.hours)[0]?.toLowerCase()}{' '}
							{minutes}{translate(codes.common.min)[0]?.toLowerCase()}{' '}
							{seconds}{translate(codes.dateTime.seconds)[0]?.toLowerCase()}
						</span>
					);
				}
			}}
		/>
	);
};

const MaintenanceCard: FC<MaintenanceProps> = (
	{
		maintenanceDateTimeUtc,
		maintenanceIsRunning,
	},
) => {
	const isRunning = normalizeBoolean(maintenanceIsRunning);
	const { translate } = useTranslationAPI();

	return (
		<PaddingBox bottomSmall className='maintenance'>
			<div
				style={{
					height: '60px',
					position: 'relative',
					zIndex: 7,
					boxShadow: '0px 4px 14px -4px rgba(97,74,6,1)',
					backgroundColor: yellow[0],
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
				}}
			>
				<FlexGrid center column>
					<FlexGrid gap='tiny' center>
						<ExclamationCircle fill={yellow[8]} />
						<Typography align='center'>
							{isRunning && (
								<>
									{translate(connectModuleCodes.platformHasBeenDisabledSince)}{' '}{formatMaintenanceDateTime(maintenanceDateTimeUtc)}
								</>
							)}
							{!isRunning && (
								<MaintenanceCountdown maintenanceDateTimeUtc={maintenanceDateTimeUtc} />
							)}
						</Typography>
					</FlexGrid>
				</FlexGrid>
			</div>
		</PaddingBox>
	);
};

const MaintenancePortal: FC<MaintenanceProps> = (props) => {
	const el = document.querySelector('#maintenance-portal');

	return el !== null ? ReactDom.createPortal(<MaintenanceCard {...props} />, el) : null;
};

export const Maintenance = () => {
	const { isAuthenticated } = useUserPageContextState();
	const [maintenanceData, setMaintenanceData] = useState<MaintenanceProps>();
	const {
		data: isWarningModalDisplayed,
		setData: serIsWarningModalDisplayed,
	} = useStoragePerUserData<boolean>(MAINTENANCE_MODAL_WARNING_KEY, false);

	const [isModalOpen, setIsModalOpen] = useState(!isWarningModalDisplayed);

	useEffect(() => {
		const unSubscribe = syncManager.on('React', MAINTENANCE_COOKIES_CHANGED, (data: MaintenanceProps) => {
			setMaintenanceData(data);
		});

		return () => {
			unSubscribe();
		};
	}, []);

	if (!isAuthenticated || !maintenanceData?.maintenanceDateTimeUtc) return null;

	return (
		<>
			<MaintenancePortal {...maintenanceData} />
			<ModalBox
				open={isModalOpen}
				size='small'
				onClose={() => {
					setIsModalOpen(false);
					serIsWarningModalDisplayed(true);
				}}
				render={(modalRenderData) => (
					<MaintenanceWarningModal
						modalRenderProps={modalRenderData}
						maintenanceProps={maintenanceData}
					/>
				)}
			/>
		</>
	);
};
